import {
  AutoCompleteAutomations,
  ISelectType,
  ITicketAutomation,
  Modal,
  useFormContext,
} from "@4uhub/lib4uhub";
import { Box, Button, Grid, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useTemplateWhats } from "../../../store/contexts/TemplateWhatsContext";
import { BUTTONS_OPTIONS } from "../../../components/WhatsappTemplate/Buttons/SelectButtons/SelectButtons";
import FormInformation from "../../../components/FormInformation/FormInformation";
import { GlobalAxios } from "../../../axios/axios-config";
import { TWhatsappTemplateForm } from "./whatsappTemplateSchema";
import {
  getNormalizedWhatsappActionTypes,
  isReagendamentoAutomationCode,
} from "../../../utils/ticketAutomation";

const translationPath = "chatBot.page.templates.whatsapp_template.";

export const WhatsappTemplateAutomation = memo(() => {
  const { t } = useTranslation();

  const { id } = useParams();

  const {
    fixDataHandler,
    setFixedCustomButton,
    setFixedCtaButton,
    removeFixedCustomButton,
    deleteCtaHandler,
    updateBasicData,
    fetchActionTypes,
    software,
  } = useTemplateWhats();

  const [open, setOpen] = useState(false);

  const [oldAutomation, setOldAutomation] = useState<ITicketAutomation | null>(
    null
  );

  const [newAutomation, setNewAutomation] = useState<ITicketAutomation | null>(
    null
  );

  const params = useMemo(
    () => ({
      AllowWhatsappTemplate: true,
      Software: software,
      Enable: true,
    }),
    [software]
  );

  const { getValues, setValue, reset, resetField } =
    useFormContext<TWhatsappTemplateForm>();

  const removeFixedCustomButtons = useCallback(() => {
    removeFixedCustomButton(0);
    removeFixedCustomButton(1);
  }, [removeFixedCustomButton]);

  const addFixedCustomButtons = useCallback(() => {
    removeFixedCustomButtons();
    setValue("ctaButtons", []);
    setValue("customButtons", []);
    deleteCtaHandler("1", 0);
    fixDataHandler(true);
  }, [
    fixDataHandler,
    setValue,
    removeFixedCustomButtons,
    setFixedCustomButton,
    deleteCtaHandler,
  ]);

  const valueChangeHandler = useCallback(
    (value: ITicketAutomation | null) => {
      if (value) {
        fetchActionTypes(
          getNormalizedWhatsappActionTypes(value.code, value.actionTypes),
        );
      }
      const newCtaButton = {
        id: "1",
        actionType: BUTTONS_OPTIONS.find((btn) => btn.id === "2")!,
        order: 1,
        text: "Clique e responda",
        payload: "",
      };

      switch (value?.code) {
        case "1":
          addFixedCustomButtons();
          break;
        case "2":
        case "6":
          fixDataHandler(true);
          break;
        case "4":
        case "5":
        case "9":
        case "10":
        case "11":
          addFixedCustomButtons();
          break;
        case "12":
        case "13":
        case "14":
          removeFixedCustomButtons();
          setValue("customButtons", []);
          setValue("ctaButtons", []);
          deleteCtaHandler("1", 0);
          fixDataHandler(true);
          break;
        case "8":
          removeFixedCustomButtons();
          setValue("customButtons", []);
          setValue("ctaButtons", []);
          deleteCtaHandler("1", 0);
          fixDataHandler(false);
          break;
        default:
          removeFixedCustomButtons();
          fixDataHandler(isReagendamentoAutomationCode(value?.code));
          break;
      }

      if (oldAutomation) {
        setOpen(true);
        setValue("ticketAutomation", oldAutomation);
      } else {
        setOldAutomation(value);
      }
      setNewAutomation(value);
    },
    [
      fixDataHandler,
      setFixedCtaButton,
      addFixedCustomButtons,
      removeFixedCustomButtons,
      deleteCtaHandler,
      setValue,
      t,
      oldAutomation,
    ]
  );

  const closeHandler = useCallback(() => {
    valueChangeHandler(oldAutomation);
    setOpen(false);
  }, [oldAutomation, valueChangeHandler]);

  const resetFormHandler = useCallback(() => {
    if (newAutomation) {
      setValue("ticketAutomation", newAutomation);
      setValue("body", "");
      setValue("footer", "");
      setValue("title", "");
      setValue("name", "");
      resetField("ticketService");
      setValue("bodyExamples", []);
      updateBasicData("body", "");
    } else {
      reset({ ticketAutomation: undefined });
    }
    setOldAutomation(newAutomation);
    setOpen(false);
  }, [reset, resetField, setValue, updateBasicData, newAutomation]);

  useEffect(() => {
    if (!oldAutomation) {
      const automation = getValues("ticketAutomation");
      if (
        automation &&
        (automation.code === "1" ||
          automation.code === "2" ||
          automation.code === "3" ||
          automation.code === "5" ||
          automation.code === "8")
      ) {
        fixDataHandler(true);
      }
    }
  }, [oldAutomation, getValues, fixDataHandler]);

  useEffect(() => {
    if (id) {
      const raw = getValues("ticketAutomation")?.actionTypes ?? [];
      const selected: ISelectType[] = raw.map(({ id, name, code }) => ({
        id,
        name,
        code: code ?? undefined,
      }));

      fetchActionTypes(
        getNormalizedWhatsappActionTypes(
          getValues("ticketAutomation")?.code,
          selected,
        ),
      );
    }
  }, [getValues, fetchActionTypes]);
  if (id) {
    return (
      <Grid item xs={12} sm={6}>
        <FormInformation
          label={t(translationPath + "automation")}
          data={getValues("ticketAutomation").name}
          minHeight={40}
        />
      </Grid>
    );
  }

  return (
    <>
      <Modal
        open={open}
        onClose={closeHandler}
        title={`${t(translationPath + "whatsappTemplateAutomation.title")}`}
      >
        <Typography>
          {t(translationPath + "whatsappTemplateAutomation.description")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            size="small"
            fullWidth
            variant="outlined"
            onClick={closeHandler}
          >
            {t(translationPath + "whatsappTemplateAutomation.cancel")}
          </Button>
          <Button
            size="small"
            fullWidth
            variant="contained"
            onClick={resetFormHandler}
          >
            {t(translationPath + "whatsappTemplateAutomation.change")}
          </Button>
        </Box>
      </Modal>
      <Grid item xs={12} sm={6}>
        <AutoCompleteAutomations
          params={params}
          name="ticketAutomation"
          label={t(translationPath + "automation")}
          getOptionLabel={(option) => option.name}
          onValueChange={valueChangeHandler}
          size="small"
          axios={GlobalAxios}
          fullWidth
        />
      </Grid>
    </>
  );
});
